<template>
  <div>
    <new-main-header
        v-if="hasNewLeftNavEnabled"
        :sub-title="pageSubtitle">
      {{ pageTitle }}
    </new-main-header>
    <main-header v-else>
      <span slot="arrow-back">
        <el-tooltip
            class="item"
            effect="dark"
            content="Back to Start Page"
            placement="bottom-start">
          <router-link :to="{ name: 'CustomerHistory' }"><i class="el-icon-back" /> </router-link>
        </el-tooltip>
      </span>
      <span slot="page-title">{{ pageTitle }}</span>
      <span slot="sub-title">{{ pageSubtitle }}</span>
    </main-header>
    <main-content>
      <div
          slot="main-content"
          class="main-content">
        <div v-loading="loading">
          <div v-if="!event.ding_id">
            <el-alert>No event found: {{ $route.params.id }}</el-alert>
          </div>
          <div v-if="event.ding_id">
            <div slot="horizontal-menu">
              <el-menu
                  v-if="false"
                  default-active="1"
                  class="ring-menu--horizontal"
                  mode="horizontal">
                <el-menu-item
                    v-scroll-to="{
                      el: '#event-details',
                      container: '#main-content',
                      duration: 500,
                      easing: 'linear'}"
                    index="1">
                  Event Details
                </el-menu-item>
                <el-menu-item
                    v-scroll-to="{
                      el: '#event-bitrate',
                      container: '#main-content',
                      duration: 500,
                      easing: 'linear'}"
                    index="4">
                  Bitrate Data
                </el-menu-item>
              </el-menu>
            </div>
            <div>
              <el-row
                  id="event-details"
                  :gutter="20">
                <el-col :span="12">
                  <el-card>
                    <div
                        slot="header"
                        class="clearfix">
                      <span class="header-item">{{ $t('common.event') }} - {{ event.type | capitalize }}</span>
                    </div>
                    <div
                        class="card-content"
                        style="height: 480px">
                      <el-row :gutter="20">
                        <el-col :span="2">
                          <div class="event-status">
                            <el-tooltip :content="'The event is ' + event.metadata.status | capitalize">
                              <i
                                  class="el-icon-bell"
                                  :class="event.metadata.status"></i>
                            </el-tooltip>
                          </div>
                        </el-col>
                        <el-col :span="22">
                          <div class="event-row">
                            <div class="event-row-label">
                              Created
                            </div>
                            <div class="event-row-value">
                              <time-zone-formatter :current-time="event.created_at" />
                            </div>
                          </div>
                          <div class="event-row">
                            <div class="event-row-label">
                              Type
                            </div>
                            <div class="event-row-value">
                              {{ event.type | capitalize }}
                            </div>
                          </div>
                          <div class="event-row">
                            <div class="event-row-label">
                              Favorite
                            </div>
                            <div class="event-row-value">
                              <i
                                  v-if="event.is_favorite"
                                  class="el-icon-star-on"
                                  style="color: orange;"></i>
                              <i
                                  v-else
                                  class="el-icon-star-off"
                                  style="color: red;"></i>
                            </div>
                          </div>
                          <div class="event-row">
                            <div class="event-row-label">
                              Metadata
                            </div>
                            <div class="event-row-value">
                              <ul>
                                <li
                                    v-for="(value, key) in event.metadata"
                                    :key="key">
                                  <div class="flex-row">
                                    <span class="flex-label">{{ key | capitalize }}</span>
                                    <span
                                        v-if="key === 'duration'"
                                        class="flex-value"><b>{{ Number(value).toPrecision() }}</b></span>
                                    <span
                                        v-else-if="key === 'detection_types'"
                                        class="flex-value">
                                      <b>{{ getDetectionTypes(value) }}</b>
                                    </span>
                                    <span
                                        v-else
                                        class="flex-value"><b>{{ (value || 'No' ) | capitalize }}</b></span>
                                  </div>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </el-col>
                      </el-row>
                    </div>
                  </el-card>
                </el-col>
                <el-col :span="12">
                  <event-device
                      :event-id="event.ding_id"
                      :doorbot-id="event.doorbot_id"
                      :customer-id="$route.params.customer_id"
                      @loaded="({device}) => readDeviceSettings(device)" />
                </el-col>
              </el-row>

              <el-row
                  id="event-bitrate"
                  :gutter="20">
                <el-col :span="24">
                  <event-bitrate :event="event" />
                </el-col>
              </el-row>
              <el-row
                  id="event-pns"
                  :gutter="20">
                <el-col :span="24">
                  <event-pns :event-id="event.ding_id" />
                </el-col>
              </el-row>
            </div>
          </div>
        </div>
      </div>
    </main-content>
  </div>
</template>

<script>
import { capitalize, get, isEmpty } from "lodash";
import MainContent from "@/containers/Core/MainContent";
import MainHeader from "@/containers/Core/Header/MainHeader";
import NewMainHeader from "@/NewUI/containers/core/header/MainHeader";

import EventDevice from "./EventDeviceData";
import EventBitrate from "./EventBitrate";
import EventPns from "./EventPns";

import { createNamespacedHelpers } from "vuex";
const { mapActions: eventActions } = createNamespacedHelpers("event");
const { mapMutations } = createNamespacedHelpers("db/device");
import permission from "@/mixins/permission";

export default {
  name: "Event",
  components: {
    MainContent,
    MainHeader,
    EventDevice,
    EventBitrate,
    EventPns,
    NewMainHeader,
  },
  mixins: [permission],
  data() {
    return {
      loading: false,
      hasAuthorization: false,
      event: {},
      deviceSettings: {},
    };
  },
  computed: {
    hasNewLeftNavEnabled() {
      return this.hasPermission("NewUI", "leftNav");
    },
    pageTitle() {
      return `${capitalize(this.event.type)} Details`;
    },
    pageSubtitle() {
      return `ID # ${get(this.event, "ding_id", "Not Found")}`;
    },
  },
  watch: {
    "$route.params.id": function () {
      this.loadEventData();
    },
  },
  created() {
    this.loadEventData();
  },
  beforeRouteLeave(to, from, next) {
    this.resetState();
    next();
  },
  methods: {
    ...mapMutations(["resetState"]),
    ...eventActions(["fetchEvent"]),
    getDetectionTypes(detectionTypes) {
      if (isEmpty(detectionTypes)) {
        return "No";
      }

      let dt = detectionTypes;

      if (Array.isArray(dt)) {
        dt = dt
          .map((detType) => detType["detection_type"])
          .map((detType) => capitalize(detType))
          .join(", ");
      }

      return dt.replace("_", " ");
    },
    readDeviceSettings(device) {
      let type = device.type;
      this.deviceSettings = get(device, `${type}_firmware_settings`, {});
    },
    loadEventData() {
      this.loading = true;
      this.fetchEvent({
        eventId: this.$route.params.id,
        user_id: this.$route.params.customer_id,
      })
        .then((response) => {
          this.event = response.data;
        })
        .catch((error) => {
          this.error = error;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.main-content {
  margin-top: 30px;
}
.event-status {
  font-size: 2rem;
  text-align: center;
  i.completed {
    color: green;
  }
  i.timed_out {
    color: red;
  }
}
.event-row {
  border-bottom: dashed 1px #ddd;
  display: flex;
  flex-direction: row;
  .event-row-label {
    padding: 10px 0px;
    font-size: 1rem;
    flex: 2;
  }
  .event-row-value {
    padding: 10px 0px;
    font-size: 1rem;
    flex: 4;
    ul {
      margin: 0px;
      list-style: none;
      padding: 0px;
      li {
        padding: 5px 0px;
        .flex-row {
          display: flex;
          flex-direction: row;
          .flex-label {
            flex: 1;
          }
          .flex-value {
            flex: 2;
          }
        }
      }
    }
  }
}
</style>
